import type { HTMLAttributes } from 'react'
import { useContext } from 'react'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'

import { DragHandleContext } from './TableRow'

// Removed CSS import since we're disabling transforms

interface TableDragHandleProps extends HTMLAttributes<HTMLDivElement> {
  item: { type: string; id: string }
  isDraggable: boolean
  className?: string
  // When true, the grip rests semi-visible instead of fully hidden until hover.
  // Used in the client portal so non-power-users can find the reorder handle.
  prominent?: boolean
}

function TableDragHandleView({
  isDraggable,
  className,
  prominent = false,
  attributes,
  listeners,
  setNodeRef,
  ...rest
}: Omit<TableDragHandleProps, 'item'> & {
  attributes?: ReturnType<typeof useSortable>['attributes']
  listeners?: ReturnType<typeof useSortable>['listeners']
  setNodeRef?: (node: HTMLDivElement | null) => void
}) {
  // Disable all transform animations - no visual movement
  const style = {
    transform: 'none',
    transition: 'none',
  }

  if (!isDraggable) {
    return (
      <div data-table-drag-handle className={cn('h-6 w-3', className)} {...rest} />
    )
  }

  return (
    <div
      data-table-drag-handle
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex h-6 w-6 cursor-grab select-none items-center justify-center active:cursor-grabbing',
        prominent
          ? 'opacity-0 group-hover/task-row:opacity-100 hover:opacity-100'
          : 'opacity-0 hover:opacity-100 group-hover/task-row:opacity-60',
        className,
      )}
      {...rest}
      {...attributes}
      {...listeners}
    >
      <div
        className={cn(
          'h-4 w-1 rounded-full',
          prominent
            ? 'bg-muted-foreground/60 group-hover/task-row:bg-muted-foreground/80'
            : 'bg-muted-foreground/40 group-hover/task-row:bg-muted-foreground/60',
        )}
      />
    </div>
  )
}

function SortableTableDragHandle({
  item,
  isDraggable,
  className,
  ...rest
}: TableDragHandleProps) {
  const { attributes, listeners, setNodeRef } = useSortable({
    id: item.id,
    disabled: !isDraggable,
  })

  return (
    <TableDragHandleView
      isDraggable={isDraggable}
      className={className}
      attributes={attributes}
      listeners={listeners}
      setNodeRef={setNodeRef}
      {...rest}
    />
  )
}

export function TableDragHandle(props: TableDragHandleProps) {
  const dragContext = useContext(DragHandleContext)

  if (
    dragContext &&
    (!props.isDraggable || dragContext.attributes || dragContext.listeners)
  ) {
    const viewProps = { ...props } as Partial<TableDragHandleProps>
    delete viewProps.item

    return (
      <TableDragHandleView
        {...(viewProps as Omit<TableDragHandleProps, 'item'>)}
        isDraggable={dragContext.isDraggable && props.isDraggable}
        attributes={dragContext.attributes}
        listeners={dragContext.listeners}
      />
    )
  }

  return <SortableTableDragHandle {...props} />
}
